import { useEffect, useRef } from 'react';
import './AnimatedLogoMark.css';

type AnimatedLogoMarkProps = {
  className?: string;
  size?: number;
};

export function AnimatedLogoMark({ className = '', size = 64 }: AnimatedLogoMarkProps) {
  const shellRef = useRef<HTMLSpanElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const greetingRef = useRef(false);
  const mirroredRef = useRef(false);
  const rafRef = useRef(0);
  const lastPointerXRef = useRef(
    typeof window !== 'undefined' ? window.innerWidth / 2 : 0
  );

  useEffect(() => {
    const shell = shellRef.current;
    const img = imgRef.current;
    if (!shell || !img) return;

    const setMirror = (mirror: boolean) => {
      if (mirroredRef.current === mirror) return;
      mirroredRef.current = mirror;
      img.style.transform = mirror ? 'scaleX(-1)' : 'scaleX(1)';
    };

    const lookFromPointer = (clientX: number) => {
      if (greetingRef.current) return;
      const rect = shell.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      setMirror(clientX < cx);
    };

    const playGreet = () => {
      if (greetingRef.current) return;
      greetingRef.current = true;
      shell.classList.add('logo-mark--greet');
      setMirror(false);
      window.setTimeout(() => {
        shell.classList.remove('logo-mark--greet');
        greetingRef.current = false;
        lookFromPointer(lastPointerXRef.current);
      }, 2600);
    };

    const onMove = (e: MouseEvent) => {
      lastPointerXRef.current = e.clientX;
      if (rafRef.current) return;
      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = 0;
        lookFromPointer(e.clientX);
      });
    };

    document.addEventListener('mousemove', onMove, { passive: true });

    const initialTimer = window.setTimeout(playGreet, 2800);
    let greetTimer = 0;

    const scheduleGreet = () => {
      const delay = 10000 + Math.random() * 8000;
      greetTimer = window.setTimeout(() => {
        playGreet();
        scheduleGreet();
      }, delay);
    };
    scheduleGreet();

    return () => {
      document.removeEventListener('mousemove', onMove);
      window.clearTimeout(initialTimer);
      window.clearTimeout(greetTimer);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <span
      className={`logo-mark logo-mark--animated ${className}`.trim()}
      style={{ width: size, height: size }}
      aria-hidden
    >
      <span ref={shellRef} className="logo-mark__shell">
        <span className="logo-mark__wrap">
          <img ref={imgRef} src="/logo.png" alt="" width={size} height={size} />
        </span>
      </span>
    </span>
  );
}
